/**
 * @file ResponsiveList.tsx
 * @description Список записи из одного описания колонок (phase19.31, шаг О4;
 * SC46, uiux-design.md §4): от 1024 — таблица на `ui/table`, до 1024 —
 * компактный список. Плюс состояния: заготовки при загрузке, ошибка с
 * «Повторить», «пусто» и «пусто под фильтром» со сбросом (тексты — страницы).
 *
 * Раскладка выбирается медиазапросом, а не двумя копиями разметки: в DOM
 * одна версия списка, ссылки и счётчики не двоятся. `layout="compact"` —
 * всегда список (узкая колонка сводки, где таблица не помещается).
 *
 * ```tsx
 * <ResponsiveList
 *   aria-label="Очередь сборки"
 *   rows={tasks}
 *   columns={PICKING_QUEUE_COLUMNS}
 *   rowKey={(t) => t.id}
 *   rowHref={(t) => `${base}/picking/${t.id}`}
 *   rowActions={(t) => [{ key: "print", label: "Лист комплектации", icon: Printer, run: () => print(t) }]}
 *   loading={queue.loading}
 *   error={queue.error}
 *   onRetry={queue.refresh}
 *   filtered={!!queue.search}
 *   empty={{ icon: ClipboardList, title: "Заданий на сборку нет" }}
 *   filteredEmpty={{ icon: Search, title: "Ничего не нашлось", onReset: clear }}
 * />
 * ```
 */

import { ListEmpty, ListError, ListSkeleton } from "./ListStates";
import type { ListColumn, ListEmptySpec, ListFilteredEmptySpec } from "./responsive-list-types";
import { ResponsiveListCompact } from "./ResponsiveListCompact";
import { ResponsiveListTable } from "./ResponsiveListTable";
import type { RowAction } from "./RowActionsMenu";
import { BELOW_LG_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";

interface Props<T> {
  rows: T[];
  columns: ListColumn<T>[];
  rowKey: (row: T) => string;
  /** Адрес записи: строка целиком — ссылка. */
  rowHref?: (row: T) => string;
  /** Подпись ссылки строки для скринридера: «Задание по заказу Z-…». */
  rowLabel?: (row: T) => string;
  /** Пункты «⋯» строки; не задано — столбца действий нет. */
  rowActions?: (row: T) => RowAction[];
  rowActionsTitle?: (row: T) => string;
  /** Таблица: «⋯» строки 32 (`compact`, по умолчанию) или 48 (`default`, касание SC44). */
  rowActionsSize?: "compact" | "default";
  /** Таблица: строка 52 (`default`) или 56 (`double`, «основное + подпись»). */
  rowSize?: "default" | "double";
  /** Список: строка 72 (`default`) или 88 (`tall`, три линии). */
  compactRowSize?: "default" | "tall";
  /** `auto` — по ширине экрана; `compact` — всегда список. */
  layout?: "auto" | "compact";
  /** Минимальная ширина таблицы (класс), чтобы колонки не сжимались на 1024. */
  tableMinWidth?: string;
  /** Рамка компактного списка; `false` — список внутри карточки сводки. */
  framed?: boolean;
  /** Компактный список: переносить длинные названия и подписи, а не обрезать. */
  compactWrap?: boolean;
  loading?: boolean;
  /** Текст ошибки загрузки; `null` — ошибки нет. */
  error?: string | null;
  onRetry?: () => void;
  /** Список пуст из-за фильтра/поиска, а не потому что записей нет. */
  filtered?: boolean;
  empty: ListEmptySpec;
  filteredEmpty?: ListFilteredEmptySpec;
  /** Сколько строк-заготовок показать при загрузке. */
  skeletonRows?: number;
  "aria-label": string;
}

export function ResponsiveList<T>({
  rows,
  columns,
  rowKey,
  rowHref,
  rowLabel,
  rowActions,
  rowActionsTitle,
  rowActionsSize = "compact",
  rowSize = "default",
  compactRowSize = "default",
  layout = "auto",
  tableMinWidth,
  framed = true,
  compactWrap = false,
  loading = false,
  error = null,
  onRetry,
  filtered = false,
  empty,
  filteredEmpty,
  skeletonRows = 5,
  "aria-label": ariaLabel,
}: Props<T>) {
  const belowLg = useMediaQuery(BELOW_LG_QUERY);
  const compact = layout === "compact" || belowLg;
  const common = { rows, columns, rowKey, rowHref, rowLabel, rowActions, rowActionsTitle, ariaLabel };

  // Ошибка без данных — только ошибка; с данными — полоса над прежним списком.
  if (error && rows.length === 0) return <ListError message={error} onRetry={onRetry} />;
  if (loading && rows.length === 0) {
    return <ListSkeleton rows={skeletonRows} compact={compact} tall={compactRowSize === "tall"} />;
  }
  if (rows.length === 0) {
    return <ListEmpty spec={filtered && filteredEmpty ? filteredEmpty : empty} filtered={filtered} />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-3" aria-busy={loading || undefined}>
      {error ? <ListError message={error} onRetry={onRetry} /> : null}
      {compact ? (
        <ResponsiveListCompact {...common} rowSize={compactRowSize} framed={framed} wrap={compactWrap} />
      ) : (
        <ResponsiveListTable {...common} rowSize={rowSize} minWidth={tableMinWidth} actionsSize={rowActionsSize} />
      )}
    </div>
  );
}
